import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import {
  ExclamationTriangleIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { financeApi } from '../../api/financeApi';
import { CURRENCY } from '../../config/currency';

interface AlertItem {
  id: number;
  type: string;
  severity: 'INFO' | 'WARNING' | 'CRITICAL';
  title: string;
  message: string;
  read: boolean;
  relatedEntityType?: string | null;
  relatedEntityId?: number | null;
  createdAt: string;
}

interface AlertPanelProps {
  open: boolean;
  onClose: () => void;
}

const SEVERITY_COLORS: Record<string, string> = {
  INFO: '#1E8E5A',
  WARNING: '#E0891E',
  CRITICAL: '#D2453C',
};

const ALERT_TYPE_ICONS: Record<string, React.ElementType> = {
  BUDGET_EXCEEDED: ExclamationTriangleIcon,
  BUDGET_WARNING: ExclamationTriangleIcon,
  FOOD_COST_HIGH: ChartBarIcon,
  PAYMENT_OVERDUE: XCircleIcon,
  PAYMENT_DUE_SOON: InformationCircleIcon,
  EXPENSE_ANOMALY: ExclamationTriangleIcon,
  SETTLEMENT_PENDING: InformationCircleIcon,
};

function getRelativeTime(dateStr: string): string {
  try {
    const now = Date.now();
    const then = new Date(dateStr).getTime();
    const diff = now - then;
    const minutes = Math.floor(diff / 60_000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(dateStr).toLocaleDateString(CURRENCY.locale, { day: '2-digit', month: 'short' });
  } catch {
    return '';
  }
}

function getEntityRoute(entityType?: string | null, entityId?: number | null): string | null {
  if (!entityType || !entityId) return null;
  switch (entityType) {
    case 'EXPENSE':
      return '/vendor/finance/expenses';
    case 'CATEGORY':
      return '/vendor/finance/settings';
    case 'PAYABLE':
      return '/vendor/finance/payables';
    case 'INCOME':
      return '/vendor/finance/income';
    default:
      return '/vendor/finance';
  }
}

const AlertPanel: React.FC<AlertPanelProps> = ({ open, onClose }) => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState<AlertItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await financeApi.getAlerts({ size: 20, sort: 'createdAt,desc' });
      const list = Array.isArray(res.data) ? res.data : res.data?.content || [];
      setAlerts(list);
    } catch {
      // Silent fail
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchAlerts();
    }
  }, [open, fetchAlerts]);

  const handleMarkAllRead = async () => {
    setMarkingAll(true);
    try {
      await financeApi.markAllAlertsRead();
      setAlerts((prev) => prev.map((a) => ({ ...a, read: true })));
      toast.success('All alerts marked as read');
    } catch {
      toast.error('Failed to mark alerts read');
    } finally {
      setMarkingAll(false);
    }
  };

  const handleAlertClick = async (alert: AlertItem) => {
    // Mark as read
    if (!alert.read) {
      try {
        await financeApi.markAlertRead(alert.id);
        setAlerts((prev) =>
          prev.map((a) => (a.id === alert.id ? { ...a, read: true } : a))
        );
      } catch {
        // Silent
      }
    }

    // Navigate if entity link exists
    const route = getEntityRoute(alert.relatedEntityType, alert.relatedEntityId);
    if (route) {
      onClose();
      navigate(route);
    }
  };

  if (!open) return null;

  const unreadCount = alerts.filter((a) => !a.read).length;

  return (
    <Box
      sx={{
        position: 'absolute',
        top: '100%',
        right: 0,
        mt: 0.75,
        width: { xs: 'calc(100vw - 32px)', sm: 360 },
        maxWidth: 360,
        maxHeight: '60vh',
        overflowY: 'auto',
        backgroundColor: theme.components?.card?.background || '#FFFFFF',
        border: `1px solid ${theme.components?.card?.border || '#E0E0E0'}`,
        borderRadius: '12px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        zIndex: 1200,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 1.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: `1px solid ${theme.gray?.[200] || '#E8E5E0'}`,
          position: 'sticky',
          top: 0,
          backgroundColor: theme.components?.card?.background || '#FFFFFF',
          zIndex: 1,
          borderTopLeftRadius: '12px',
          borderTopRightRadius: '12px',
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: theme.text }}>
          Alerts
          {unreadCount > 0 && (
            <span
              style={{
                marginLeft: 6,
                fontSize: '0.7rem',
                fontWeight: 600,
                color: 'var(--status-bad)',
              }}
            >
              ({unreadCount} new)
            </span>
          )}
        </Typography>
        {unreadCount > 0 && (
          <button
            onClick={handleMarkAllRead}
            disabled={markingAll}
            style={{
              fontSize: '0.75rem',
              fontWeight: 600,
              color: theme.primary || 'var(--action)',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px 8px',
              borderRadius: 6,
              fontFamily: 'inherit',
              opacity: markingAll ? 0.5 : 1,
            }}
          >
            Mark all read
          </button>
        )}
      </Box>

      {/* Content */}
      <Box sx={{ p: 0.5 }}>
        {loading ? (
          // Loading skeleton
          Array.from({ length: 3 }).map((_, i) => (
            <Box key={i} sx={{ p: 1.5, display: 'flex', gap: 1.5 }}>
              <Skeleton variant="circular" width={32} height={32} sx={{ flexShrink: 0, bgcolor: theme.components?.skeleton?.base || '#E0E0E0' }} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="70%" sx={{ bgcolor: theme.components?.skeleton?.base || '#E0E0E0' }} />
                <Skeleton variant="text" width="90%" sx={{ bgcolor: theme.components?.skeleton?.base || '#E0E0E0' }} />
              </Box>
            </Box>
          ))
        ) : alerts.length === 0 ? (
          // Empty state
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <CheckCircleIcon style={{ width: 32, height: 32, color: theme.success || '#4CAF50', margin: '0 auto 8px' }} />
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: theme.text }}>
              No alerts
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: theme.gray?.[500] || '#666', mt: 0.5 }}>
              You&apos;re all caught up!
            </Typography>
          </Box>
        ) : (
          // Alert list
          alerts.map((alert) => {
            const severityColor = SEVERITY_COLORS[alert.severity] || '#666';
            const IconComponent = ALERT_TYPE_ICONS[alert.type] || InformationCircleIcon;
            const hasLink = getEntityRoute(alert.relatedEntityType, alert.relatedEntityId);

            return (
              <Box
                key={alert.id}
                onClick={() => handleAlertClick(alert)}
                sx={{
                  display: 'flex',
                  gap: 1.25,
                  p: 1.25,
                  mx: 0.5,
                  mb: 0.25,
                  borderRadius: '10px',
                  borderLeft: `3px solid ${severityColor}`,
                  backgroundColor: alert.read
                    ? 'transparent'
                    : `${severityColor}08`,
                  cursor: hasLink ? 'pointer' : 'default',
                  transition: 'background-color 0.15s ease',
                  '&:hover': hasLink
                    ? { backgroundColor: `${theme.primary}08` }
                    : {},
                }}
              >
                {/* Icon */}
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '8px',
                    backgroundColor: `${severityColor}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <IconComponent style={{ width: 16, height: 16, color: severityColor }} />
                </Box>

                {/* Content */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                    <Typography
                      sx={{
                        fontWeight: alert.read ? 500 : 700,
                        fontSize: '0.8rem',
                        color: theme.text,
                        lineHeight: 1.3,
                      }}
                    >
                      {alert.title}
                    </Typography>
                    {!alert.read && (
                      <Box
                        sx={{
                          width: 7,
                          height: 7,
                          borderRadius: '50%',
                          backgroundColor: severityColor,
                          flexShrink: 0,
                          mt: 0.5,
                        }}
                      />
                    )}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '0.72rem',
                      color: theme.gray?.[500] || '#666',
                      lineHeight: 1.4,
                      mt: 0.25,
                    }}
                  >
                    {alert.message}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.65rem',
                      color: theme.gray?.[400] || '#999',
                      mt: 0.5,
                    }}
                  >
                    {getRelativeTime(alert.createdAt)}
                  </Typography>
                </Box>
              </Box>
            );
          })
        )}
      </Box>

      {/* Footer */}
      {!loading && alerts.length >= 20 && (
        <Box
          sx={{
            p: 1,
            borderTop: `1px solid ${theme.gray?.[200] || '#E8E5E0'}`,
            textAlign: 'center',
          }}
        >
          <button
            onClick={() => {
              onClose();
              navigate('/vendor/finance/settings');
            }}
            style={{
              fontSize: '0.75rem',
              fontWeight: 600,
              color: theme.primary || 'var(--action)',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '6px 12px',
              borderRadius: 6,
              fontFamily: 'inherit',
            }}
          >
            View all alerts
          </button>
        </Box>
      )}
    </Box>
  );
};

export default AlertPanel;
